"use client"

import * as Popover from "@radix-ui/react-popover"
import gsap from "gsap"
import FullMark from "images/FullMark.inline.svg"
import EmtypCircleLogo from "images/Icons/EmptyCircle.inline.svg"
import SmallArrowSVG from "images/Icons/SmallArrow.inline.svg"
import { ScreenContext } from "library/ScreenContext"
import { useScrollLock } from "library/Scroll"
import UniversalLink from "library/link"
import {
	css,
	fmobile,
	fresponsive,
	ftablet,
	keyframes,
	styled,
	unresponsive,
} from "library/styled"
import { useAnimation } from "library/useAnimation"
import useAutoHideHeader from "library/useAutoHideHeader"
import { useBetterThrottle } from "library/useBetterThrottle"
import { useContext, useEffect, useRef, useState } from "react"
import colors from "styles/colors"
import customEases from "styles/eases"
import textStyles from "styles/text"
import links from "utils/links"
import Button from "./Buttons/Button"
import LinkButton from "./Buttons/Link"

const duration = 500

const NAV_ITEMS = [
	{
		_key: "1",
		linkText: "For Brokers",
		url: links.brokers,
	},
	{
		_key: "2",
		linkText: "About",
		url: links.about,
	},
	{
		_key: "3",
		linkText: "Careers",
		url: links.careers,
	},
	{
		_key: "4",
		linkText: "Blog",
		url: links.blog,
	},
]

export default function Header() {
	const wrapperRef = useRef<HTMLDivElement>(null)
	const { mobile, tablet } = useContext(ScreenContext)
	const [menuOpenRaw, setMenuOpen] = useState(false)
	const menuOpen = useBetterThrottle(menuOpenRaw, duration)
	const lineRef1 = useRef<SVGLineElement | null>(null)
	const lineRef2 = useRef<SVGLineElement | null>(null)
	const lineRef3 = useRef<SVGLineElement | null>(null)
	const menuRef = useRef<HTMLDivElement | null>(null)
	const overlayRef = useRef<HTMLDivElement | null>(null)
	const overlayTimelineRef = useRef<gsap.core.Timeline | null>(null)

	useScrollLock("lock", menuOpen && (mobile || tablet))
	useAutoHideHeader(wrapperRef)

	const buttonTimeline = useAnimation(() => {
		if (!mobile) return null
		const tl = gsap.timeline({
			paused: true,
			defaults: {
				duration: duration / 1000,
				ease: "power2.inOut",
			},
		})

		tl.to(
			lineRef1.current,
			{
				attr: {
					x1: 2,
					x2: 12,
					y1: 2,
					y2: 12,
				},
			},
			0.1,
		)

		tl.to(
			lineRef2.current,
			{
				attr: {
					x1: 7,
					x2: 7,
				},
			},
			0,
		)

		tl.to(
			lineRef3.current,
			{
				attr: {
					x1: 2,
					x2: 12,
					y1: 12,
					y2: 2,
				},
			},
			0.1,
		)

		return tl
	}, [mobile])

	useEffect(() => {
		if (!mobile && !tablet) {
			setMenuOpen(false)
		}
	}, [mobile, tablet])

	useEffect(() => {
		if (!overlayRef.current || !mobile) return

		overlayTimelineRef.current = gsap
			.timeline({ paused: true })
			.fromTo(
				overlayRef.current,
				{ opacity: 0, display: "block" },
				{ opacity: 1, duration: duration / 1000, ease: "power2.out" },
			)

		const closeTimeline = gsap
			.timeline({ paused: true })
			.to(overlayRef.current, {
				opacity: 0,
				duration: duration / 1000,
				ease: "power2.in",
				onComplete: () => {
					if (overlayRef.current) {
						gsap.set(overlayRef.current, { display: "none" })
					}
				},
			})

		overlayTimelineRef.current.closeTimeline = closeTimeline

		return () => {
			if (overlayTimelineRef.current) {
				overlayTimelineRef.current.kill()
				overlayTimelineRef.current.closeTimeline.kill()
			}
		}
	}, [mobile])

	useEffect(() => {
		if (menuOpen) {
			buttonTimeline.result?.play()
			if (menuRef.current) {
				gsap.set(menuRef.current, { display: "flex" })
			}
			if (mobile && overlayTimelineRef.current) {
				gsap.set(overlayRef.current, { display: "block" })
				overlayTimelineRef.current.seek(0).play()
			}
		} else {
			buttonTimeline.result?.reverse()
			if (mobile && overlayTimelineRef.current) {
				overlayTimelineRef.current.closeTimeline.seek(0).play()
			}
		}
	}, [buttonTimeline, menuOpen, mobile])

	return (
		<Popover.Root open={menuOpen} onOpenChange={setMenuOpen}>
			<SlideDown />
			<SlideUp />
			<FadeIn />
			<FadeOut />

			{mobile && <BlurOverlay ref={overlayRef} />}

			<Wrapper ref={wrapperRef}>
				{mobile && (
					<Menu ref={menuRef} data-state={menuOpen ? "open" : "closed"}>
						<MenuContent>
							<NavLinks>
								{NAV_ITEMS?.map((link) => (
									<div
										key={link._key}
										onClick={() => setMenuOpen(false)}
										onKeyDown={() => setMenuOpen(false)}
									>
										<NavLink href={link.url ?? links.home}>
											{link.linkText}
											<SmallArrow />
										</NavLink>
									</div>
								))}
							</NavLinks>

							<ButtonContainer onClick={() => setMenuOpen(false)}>
								<Button
									href={links.signin}
									variant="light"
									size="large"
									simple={false}
								>
									Log in
								</Button>
							</ButtonContainer>
						</MenuContent>
					</Menu>
				)}

				<DesktopHeader>
					<LogoWrapper href={links.home} aria-label="home">
						<Logo />
					</LogoWrapper>
					<Container>
						<Links>
							<LinkButton href={links.brokers} variant="light" size="small">
								For Brokers
							</LinkButton>
							<VerticalLine />
							<LinkButton href={links.about} variant="light" size="small">
								About
							</LinkButton>
							<VerticalLine />
							<LinkButton href={links.careers} variant="light" size="small">
								Careers
							</LinkButton>
							<VerticalLine />
							<LinkButton href={links.blog} variant="light" size="small">
								Blog
							</LinkButton>
						</Links>
						<ButtonWrapper>
							<Button
								href={links.signin}
								variant="light"
								size="medium"
								simple={true}
							>
								Log in
							</Button>
						</ButtonWrapper>
					</Container>
				</DesktopHeader>

				<MobileHeaderBar>
					<LogoWrapper href={links.home} aria-label="home">
						<Logo />
					</LogoWrapper>

					<Hamburger type="button" aria-label="navigation menu">
						<CircleOuter className="circle-logo" />
						<Lines viewBox="0 0 14 14" overflow="visible">
							<line
								ref={lineRef1}
								x1={0}
								x2={14}
								y1={2}
								y2={2}
								style={{ stroke: "var(--green400)", strokeWidth: "1.5px" }}
							/>
							<line
								ref={lineRef2}
								x1={0}
								x2={14}
								y1={7}
								y2={7}
								style={{ stroke: "var(--green400)", strokeWidth: "1.5px" }}
							/>
							<line
								ref={lineRef3}
								x1={0}
								x2={14}
								y1={12}
								y2={12}
								style={{ stroke: "var(--green400)", strokeWidth: "1.5px" }}
							/>
						</Lines>
					</Hamburger>
				</MobileHeaderBar>
			</Wrapper>
		</Popover.Root>
	)
}

const SlideDown = keyframes`
  from {
    transform: translateY(-150%);
  }
  to {
    transform: translateY(0);
  }
`

const SlideUp = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-150%);
  }
`

const FadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const FadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const BlurOverlay = styled("div", {
	position: "fixed",
	top: 0,
	right: 0,
	bottom: 0,
	left: 0,
	width: "100vw",
	height: "100vh",
	zIndex: 19,
	backdropFilter: "blur(8px)",
	WebkitBackdropFilter: "blur(8px)",
	backgroundColor: "rgba(255, 255, 255, 0.5)",
	opacity: 0,
	display: "none",
})

const Wrapper = styled("header", {
	...fresponsive(css`
		display: flex;
		flex-direction: column;
		position: sticky;
		z-index: 20;
		top: 0;
		width: 100%;
		max-width: 1440px;
		margin: 0 auto;
		height: 72px;
		view-transition-name: header;
	`),

	...ftablet(css`
		max-width: 960px;
		width: 960px;
	`),

	...fmobile(css`
		max-width: 327px;
		width: 327px;
	`),
})

const DesktopHeader = styled("div", {
	...fresponsive(css`
		display: flex;
		justify-content: space-between;
		width: 100%;
		height: 100%;
	`),
	...fmobile(css`
		display: none;
	`),
})

const Container = styled(
	"div",
	fresponsive(css`
		display: flex;
		align-items: center;
		justify-content: space-between;
		background-color: ${colors.offWhite};
		border: 1px solid ${colors.green100};
		border-left: none;
		height: 100%;
	`),
)

const VerticalLine = styled(
	"div",
	fresponsive(css`
		width: 1px;
		height: 9px;
		background-color: ${colors.green100};
	`),
)

const Links = styled("div", {
	...fresponsive(css`
		display: flex;
		gap: 16px;
		padding: 0 32.5px;
		border-left: 1px solid ${colors.green100};
		height: 100%;
		align-items: center;
	`),
	...ftablet(css`
		gap: 24px;
		width: 642px;
		padding: 0;
		justify-content: center;
	`),
})

const MobileHeaderBar = styled(Popover.Anchor, {
	...unresponsive(css`
		display: none;
	`),
	...fmobile(css`
		display: flex;
		justify-content: space-between;
		align-items: center;
		position: relative;
		z-index: 21;
		width: 327px;
		margin: 0 auto;
		height: 100%;
		background-color: ${colors.offWhite};
		border: 1px solid ${colors.green100};
	`),
})

const Menu = styled(Popover.Content, {
	...unresponsive(css`
		display: none;
	`),
	...fmobile(css`
		display: flex;
		flex-direction: column;
		height: fit-content;
		width: 327px;
		background-color: ${colors.offWhite};
		z-index: 20;
		border-radius: 0 0 16px 16px;
		border: 1px solid ${colors.green100};
		border-top: none;
		padding: 0 0 20px;
		outline: none;
		box-shadow: none;

		&[data-state="open"] {
			animation: ${SlideDown} ${duration}ms ${customEases.cubic.out};
			animation-fill-mode: forwards;
		}

		&[data-state="closed"] {
			animation: ${SlideUp} ${duration}ms ${customEases.cubic.in};
			animation-fill-mode: forwards;
		}
	`),
})

const Hamburger = styled(Popover.Trigger, {
	...fresponsive(css`
		display: none;
	`),
	...fmobile(css`
		position: relative;
		display: grid;
		place-items: center;
		background: none;
		border: none;
		cursor: pointer;
		width: 72px;
		height: 100%;
		border-left: 1px solid ${colors.green100};

		.circle-logo {
			position: absolute;
			width: 34px;
			height: 34px;
			left: 50%;
			top: 50%;
			transform: translate(-50%, -50%);
		}
	`),
})

const Lines = styled("svg", {
	...fmobile(css`
		margin: 8px;
		width: 14px;
		height: 14px;

		&:hover {
			cursor: pointer;
		}
	`),
})

const MenuContent = styled("div", {
	...fmobile(css`
		display: flex;
		flex-direction: column;
		padding: 48px 20px 0;
		width: 100%;
		height: 100%;
		gap: 48px;
		overflow-y: auto;
	`),
})

const NavLinks = styled("div", {
	...fmobile(css`
		${textStyles.bodyS};
		display: flex;
		flex-direction: column;
		gap: 16px;
	`),
})

const NavLink = styled(UniversalLink, {
	...fmobile(css`
		color: ${colors.green400};
		font-size: 20px;
		text-decoration: none;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-bottom: 16px;
		border-bottom: 1px solid ${colors.green100};
	`),
})

const LogoWrapper = styled(UniversalLink, {
	...fresponsive(css`
		display: flex;
		align-items: center;
		border: 1px solid ${colors.green100};
		transition: color 0.3s ${customEases.icon};
		background-color: ${colors.offWhite};

		&:hover {
			color: ${colors.green400};

			svg {
				transform: scale(1.05);
			}
		}
	`),
	...ftablet(css`
		width: 100%;
		border-right: none;
		display: grid;
		place-items: center;
	`),
	...fmobile(css`
		width: 159px;
		border: none;
	`),
})

const Logo = styled(
	FullMark,
	fresponsive(css`
		width: 50px;
		color: ${colors.green400};
		position: relative;
		display: block;
		margin: 0 32px;
		transition: transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
	`),
)

const ButtonWrapper = styled(
	"div",
	fresponsive(css`
		display: flex;
		padding: 0 32.5px;
		align-items: center;
		border-left: 1px solid ${colors.green100};
		height: 100%;
		flex-shrink: 0;
	`),
)

const ButtonContainer = styled(
	"div",
	fmobile(css`
		display: flex;
		margin-top: 20px;
	`),
)

const SmallArrow = styled(
	SmallArrowSVG,
	fresponsive(css`
		width: 8px;
		height: 8px;
	`),
)

const CircleOuter = styled(
	EmtypCircleLogo,
	fresponsive(css`
		color: ${colors.green400};
		width: 34px;
		height: 34px;
	`),
)
